import React, { useState } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";

import { materialType } from "~/constants/prop-types";

import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import DialogActions from "@mui/material/DialogActions";
import DeleteIcon from "@mui/icons-material/Delete";
import InputContextMenu from "~/components/InputContextMenu";

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(0, 0, 1, 0)
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(1)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

function Form({ onSubmit, material, canEdit, onDelete }) {
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    ...material,
    sourcePath: material.sourcePath || "https://"
  });

  function validateFormData() {
    const errors = {};

    // validate source path
    if (!/^https?:\/\/.+/.test(formData.sourcePath)) {
      errors.sourcePath = (
        <p>
          Die Internetadresse muss mit
          <i>https://</i>
          beginnen.
        </p>
      );
    }

    setValidationErrors(errors);
    return isEmpty(errors);
  }
  function handleSubmit(event) {
    event.preventDefault();
    if (validateFormData()) onSubmit(formData);
  }

  function handleChange(event) {
    const name = event.target.id;
    const { value } = event.target;

    setFormData(data => ({
      ...data,
      [name]: value
    }));
  }

  function handleDelete(event) {
    onDelete(formData);
  }

  function getSubmitButtonStyle() {
    return { marginLeft: 0, marginRight: "auto" };
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <InputContextMenu />
      <StyledFormControl variant="standard" fullWidth required>
        <InputLabel htmlFor="title">Titel</InputLabel>
        <Input onChange={handleChange} id="title" value={formData.title} />
      </StyledFormControl>
      <StyledFormControl variant="standard" fullWidth required>
        <InputLabel htmlFor="linkText">Linktext</InputLabel>
        <Input onChange={handleChange} id="linkText" value={formData.linkText} />
      </StyledFormControl>
      <FormControl variant="standard" fullWidth required error={validationErrors.sourcePath}>
        <InputLabel htmlFor="sourcePath">Internetadresse</InputLabel>
        <Input
          onChange={handleChange}
          id="sourcePath"
          value={formData.sourcePath}
          aria-describedby="sourcePath-helper-text"
        />
        {validationErrors.sourcePath && (
          <FormHelperText id="sourcePath-helper-text">{validationErrors.sourcePath}</FormHelperText>
        )}
      </FormControl>
      <StyledFormControl variant="standard" fullWidth>
        <InputLabel htmlFor="shortDescription">Beschreibung</InputLabel>
        <Input onChange={handleChange} id="shortDescription" value={formData.shortDescription} multiline />
      </StyledFormControl>
      <StyledDialogActions>
        {canEdit && (
          <StyledButton color="secondary" style={getSubmitButtonStyle()} variant="text" onClick={handleDelete}>
            <DeleteIcon />
            Entfernen
          </StyledButton>
        )}
        {canEdit ? (
          <StyledButton color="primary" type="submit">
            Speichern
          </StyledButton>
        ) : (
          <StyledButton color="primary" type="submit">
            Hinzufügen
          </StyledButton>
        )}
      </StyledDialogActions>
    </form>
  );
}

Form.propTypes = {
  canEdit: PropTypes.bool,
  onDelete: PropTypes.func,
  onSubmit: PropTypes.func,
  material: materialType
};

Form.defaultProps = {
  canEdit: false,
  onDelete: () => {}
};

export default Form;
